import { useFormik, Form, FormikProvider } from 'formik';
// material
import { Card, CardHeader, Stack, TextField, IconButton, InputAdornment, Box } from '@mui/material';

import * as Yup from 'yup';
import { useState, useContext } from 'react';
import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
// material
import { LoadingButton } from '@mui/lab';
// contexts
import { useAuth } from '../../../contexts/Auth';
import { SettingsContext } from '../../../contexts/Settings';
import { updateUser } from '../../../services/User';

export default function FormPassword() {
  const { authUser } = useAuth();
  const { authSettings } = useContext(SettingsContext);

  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);

  const RegisterSchema = Yup.object().shape({
    password: Yup.string()
      .required('Password is required')
      .min(6, 'The password must be a string with at least 6 characters'),
    passwordConfirmation: Yup.string()
      .required('Confirmation password is required')
      .oneOf([Yup.ref('password'), null], 'Passwords must match')
  });

  const formik = useFormik({
    initialValues: {
      password: '',
      passwordConfirmation: ''
    },
    validationSchema: RegisterSchema,
    onSubmit: async (values) => {
      try {
        const idToken = await authUser.getIdToken();
        const headers = {
          Authorization: `Bearer ${idToken}`,
          'Account-Key': authSettings.server.path
        };
        const data = values;
        const user = await updateUser(headers, data, authUser.uid);

        if (user.error) {
          console.error(user);
          alert('An error occurred! Please, refresh the page and try again.');
        }

        window.location.reload();
      } catch (err) {
        console.error(err);
        alert('An error occurred! Please, refresh the page and try again.');
      }
    }
  });

  const { errors, touched, handleSubmit, isSubmitting, getFieldProps } = formik;

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader title="Password" />
      <Box sx={{ m: 3 }} dir="ltr">
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <TextField
                fullWidth
                type={showPassword ? 'text' : 'password'}
                label="Password"
                {...getFieldProps('password')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton edge="end" onClick={() => setShowPassword((prev) => !prev)}>
                        <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                error={Boolean(touched.password && errors.password)}
                helperText={touched.password && errors.password}
              />

              <TextField
                fullWidth
                type={showPasswordConfirmation ? 'text' : 'password'}
                label="Password Confirmation"
                {...getFieldProps('passwordConfirmation')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        onClick={() => setShowPasswordConfirmation((prev) => !prev)}
                      >
                        <Icon icon={showPasswordConfirmation ? eyeFill : eyeOffFill} />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                error={Boolean(touched.passwordConfirmation && errors.passwordConfirmation)}
                helperText={touched.passwordConfirmation && errors.passwordConfirmation}
              />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <LoadingButton
                  size="large"
                  type="submit"
                  variant="contained"
                  loading={isSubmitting}
                >
                  Save
                </LoadingButton>
              </Box>
            </Stack>
          </Form>
        </FormikProvider>
      </Box>
    </Card>
  );
}
